import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Globe, TelegramLogo, WhatsappLogo } from '@phosphor-icons/react';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { useInlineChatHost } from '../../context/InlineChatHostContext';
import { chatMirror } from '../../services/chatMirror';
import { useTranslation } from '../../i18n/I18nContext';
import { MockWindow } from './MockWindow';
import { BrandArrowIcon } from '../../components/icons/BrandArrowIcon';
import './ChannelInbox.css';

/**
 * Знаки мессенджеров — ЗАЛИТЫЕ и в фирменном цвете.
 *
 * Контурными и в цвете текста они читались иконками «чат» и «отправить», а не
 * WhatsApp и Telegram: смысл переключателя в том, что переписка приходит из
 * ЧУЖИХ приложений, и узнаваться они должны с одного взгляда.
 *
 * Толщину задаём здесь, ЦВЕТ — в CSS. Атрибут `color` на `<svg>` — это
 * презентационный атрибут: он проигрывает любому правилу, а сегмент задаёт
 * цвет текста, который знак и наследовал (замер: `rgb(99, 99, 100)` вместо
 * зелёного).
 */
const WhatsappMark = (props) => <WhatsappLogo {...props} weight="fill" />;

const TelegramMark = (props) => <TelegramLogo {...props} weight="fill" />;

/**
 * Каналы. `label` — подпись в переключателе, `address` — что показано в хроме
 * окна: у сайта это адрес витрины дилера, у мессенджера адреса нет, и там
 * подписью служит его имя.
 */
const CHANNELS = [
  {
    value: 'website',
    label: 'Website',
    address: 'cardealer.shiftgears.ai',
    icon: Globe,
  },
  {
    value: 'whatsapp',
    label: 'WhatsApp',
    icon: WhatsappMark,
  },
  {
    value: 'telegram',
    label: 'Telegram',
    icon: TelegramMark,
  },
];

/**
 * ChannelInbox — «чат, куда бы покупатель ни написал» (#1330, пункт 7).
 *
 * ## Вид под выбранный мессенджер
 *
 * У обёртки есть модификатор канала (`--website` / `--whatsapp` / `--telegram`),
 * и переписка перекрашивается под него: фон, пузыри, шапка. Без этого чат в
 * рамке с логотипом Telegram выглядел нашим чатом с чужой иконкой, и «одна и
 * та же переписка приходит из любого мессенджера» приходилось объяснять
 * словами.
 *
 * Красим СНАРУЖИ, правилами страницы, а не настройками виджета: чат внутри —
 * настоящий `VoiceWidgetConnected`, который едет дилерам, и у него не должно
 * быть режима «выглядеть как WhatsApp».
 *
 * Переписка стоит в окне-мокапе (`MockWindow`) с логотипом канала в хроме, а
 * переключатель каналов — НАД окном: он выбирает, что показано в окне.
 * Раньше он лежал внутри чата поверх шапки, и разговор читался вперемешку с
 * настройкой страницы.
 *
 * ## Ассистент здесь НАСТОЯЩИЙ
 *
 * Внутри стоит тот же `VoiceWidgetConnected`, что работает плавающим окном,
 * поднятый в потоке страницы через `inline`. Ответы приходят от живого
 * агента: рисовать заготовленную переписку на витрине, которая только что
 * выкинула форму-пустышку, было бы ровно тем же самым.
 *
 * ## Что вкладки делают и чего НЕ делают
 *
 * Переключатель меняет ПОДАЧУ, а не транспорт, и блок пишет об этом прямо в
 * подписи под ним. Сделать иначе сегодня нельзя, и это не лень:
 *
 * - у WhatsApp в `b2b-api` вообще нет inbound — только `whatsapp-api.client.ts`
 *   с `sendText`/`sendTemplate`, вебхука не существует;
 * - ответ, отправленный в Telegram, посетитель увидит в Telegram, а не на
 *   странице.
 *
 * Продуктовое утверждение при этом честное и ровно то, что показано: один и
 * тот же ассистент отвечает в любом канале, а переписка попадает в общий
 * инбокс панели.
 *
 * ## Почему живой чат на странице ровно один
 *
 * `browserControlWS` — синглтон, и его дёргает каждый чат-embed. Два живых
 * чата на странице перезаписывают друг другу командный набор — это и есть
 * причина #859. Поэтому `ContactPage` прячет плавающий виджет: инлайновый
 * его заменяет, а не дополняет.
 *
 * Файл в `pages/shared/`, поэтому его CSS не инлайнится в Shadow DOM дилера
 * (`bootstrap.jsx` берёт глоб `components/**` и именной список `pages/*.css`).
 */
export const ChannelInbox = ({ channels = CHANNELS }) => {
  const { t } = useTranslation();
  const [active, setActive] = useState(channels[0]?.value);
  const { setHost, isClaimed, releaseClaim } = useInlineChatHost();
  const channel = channels.find((c) => c.value === active) ?? channels[0];
  const ChannelIcon = channel?.icon;

  // Публикуем выбор: мокап инбокса рядом помечает им новые сообщения.
  useEffect(() => {
    chatMirror.setChannel(active);
  }, [active]);

  // Во время звонка виджет занят голосом, но остаётся здесь и показывает
  // переписку до звонка (#1441). Взялись за чат — нажали, начали печатать или
  // выбрали канал — звонок завершается, и чат снова живой (#1417). Только ЯВНЫЕ действия: по фокусу звонок
  // обрывался, когда клавиатура просто проходила Tab'ом мимо переключателя
  // (ревью !1296). Выбор канала с клавиатуры — тоже явное действие, он идёт
  // через `onChange`.
  //
  // Слушатель нативный, а не `onPointerDownCapture`: виджет вставлен сюда
  // порталом из `App`, и синтетические события React идут по дереву
  // компонентов, а не по DOM — клик по самому виджету этот блок не видел.
  // Раньше это не мешало (на время звонка окно было пустым), а с тех пор как
  // переписка остаётся на месте (#1441), клик в чат звонок не завершал.
  const bodyRef = useRef(null);
  useEffect(() => {
    const node = bodyRef.current;
    if (!node || !isClaimed) return undefined;
    // Клавиатура и скринридер (ревью !1301): набор текста в чате — тоже
    // «взялись за чат». Tab — нет: проход фокусом мимо окна звонок не
    // обрывает (ревью !1296). Скринридер активирует `click` без
    // `pointerdown`, поэтому слушаем и его.
    // Только клавиши набора: символ, пробел, Enter, стирание. Стрелки,
    // PageUp/Home/End (читают и листают переписку), F-клавиши, CapsLock —
    // не «взялись за чат» (ревью !1301).
    const TYPING_KEYS = ['Enter', 'Backspace', 'Delete'];
    const onKeyDown = (event) => {
      // Сочетания (Ctrl+C, Cmd+A) — не набор. Alt — набор: на Mac Option даёт
      // ą, œ, «, а AltGr на Windows — это Ctrl+Alt (@, €, польские буквы).
      const altGr = event.getModifierState?.('AltGraph') || (event.ctrlKey && event.altKey);
      if (!altGr && (event.ctrlKey || event.metaKey)) return;
      if (event.key.length !== 1 && !TYPING_KEYS.includes(event.key)) return;
      releaseClaim();
    };
    node.addEventListener('pointerdown', releaseClaim, true);
    node.addEventListener('click', releaseClaim, true);
    node.addEventListener('keydown', onKeyDown, true);
    return () => {
      node.removeEventListener('pointerdown', releaseClaim, true);
      node.removeEventListener('click', releaseClaim, true);
      node.removeEventListener('keydown', onKeyDown, true);
    };
  }, [isClaimed, releaseClaim]);
  const selectChannel = (value) => {
    if (isClaimed) releaseClaim();
    setActive(value);
  };

  return (
    <div
      className={`channel-inbox channel-inbox--${active}${
        active === 'website' ? '' : ' channel-inbox--messenger'
      }`}
    >
      {/* Переключатель НАД окном, а не внутри переписки: это выбор того, что
          показано в окне, — ровно как вкладка браузера, а не часть разговора. */}
      <SegmentedControl
        value={active}
        onChange={selectChannel}
        options={channels.map(({ value, label, icon }) => ({
          value,
          label,
          icon,
        }))}
        aria-label={t('showcase.chats.channel', 'Channel')}
      />

      {/* Окно с логотипом канала в хроме: без него переписка в рамке —
          просто переписка, и непонятно, где она идёт. */}
      <MockWindow
        className="channel-inbox__window"
        label={
          <>
            {ChannelIcon && (
              <ChannelIcon size={16} weight="fill" aria-hidden="true" />
            )}
            {channel?.address ?? channel?.label}
          </>
        }
      >
        <div className="channel-inbox__body" ref={bodyRef}>
          {/* Шапка переписки — как в самом мессенджере: аватар, имя, «в сети».
              Своя, страничная: шапку виджета внутри окна мы прячем, а сам
              виджет едет дилерам и режима «выглядеть как WhatsApp» у него нет
              (#1417). На сайте её нет — там окно браузера с адресом. */}
          {active !== 'website' && (
            <div className="channel-inbox__chat-head" aria-hidden="true">
              {/* Аватар чата — наша стрелка, как лого у бизнес-аккаунта. */}
              <span className="channel-inbox__avatar">
                <BrandArrowIcon className="channel-inbox__avatar-mark" />
              </span>
              <span className="channel-inbox__contact">
                <span className="channel-inbox__contact-name">ShiftGears</span>
                <span className="channel-inbox__contact-status">
                  {t('showcase.chats.online', 'online')}
                </span>
              </span>
            </div>
          )}
          {/* Пустой контейнер: виджет сюда портирует `App`. Своего экземпляра
              тут нет намеренно — см. `InlineChatHostContext`. */}
          <div className="channel-inbox__chat" ref={setHost} />
        </div>
      </MockWindow>
    </div>
  );
};

ChannelInbox.propTypes = {
  /** Каналы переключателя. */
  channels: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      /** Адрес в хроме окна. Нет — покажем `label`. */
      address: PropTypes.string,
      icon: PropTypes.elementType,
    })
  ),
};

export default ChannelInbox;
